import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch, ApiError } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  CreateTaskPayload,
  PaginatedResponse,
  TaskComment,
  TaskDetail,
  TaskListItem,
  TaskStatus,
  UpdateTaskPayload,
} from "../types";

const TASKS_KEY = ["tasks"] as const;

export type TaskListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  priority?: string;
  companyId?: string;
  productId?: string;
  projectId?: string;
  phaseId?: string;
  stageId?: string;
  meetingId?: string;
  decisionId?: string;
  ideaId?: string;
  milestoneId?: string;
  assignedToId?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  overdue?: boolean;
};

export function useTaskList(
  params: TaskListParams = {},
): UseQueryResult<PaginatedResponse<TaskListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...TASKS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<TaskListItem>>("/tasks", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          priority: params.priority,
          companyId: params.companyId,
          productId: params.productId,
          projectId: params.projectId,
          phaseId: params.phaseId,
          stageId: params.stageId,
          meetingId: params.meetingId,
          decisionId: params.decisionId,
          ideaId: params.ideaId,
          milestoneId: params.milestoneId,
          assignedToId: params.assignedToId,
          dueDateFrom: params.dueDateFrom,
          dueDateTo: params.dueDateTo,
          overdue: params.overdue ? "true" : undefined,
        },
      }),
  });
}

export function useTask(id: string): UseQueryResult<TaskDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...TASKS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ task: TaskDetail }>(`/tasks/${id}`, {
        token: accessToken,
      });
      return res.task;
    },
  });
}

export function useCreateTask() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTaskPayload) =>
      apiFetch<{ task: TaskDetail }>("/tasks", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY] });
      qc.invalidateQueries({ queryKey: ["meetings"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["companies"] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
  });
}

export function useUpdateTask(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateTaskPayload) =>
      apiFetch<{ task: TaskDetail }>(`/tasks/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY] });
    },
  });
}

export type IncompleteActionsError = {
  code: "INCOMPLETE_ACTIONS";
  incompleteCount: number;
  message: string;
};

export function useUpdateTaskStatus() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    { task: TaskDetail } | { incompleteActions: IncompleteActionsError },
    ApiError,
    { id: string; status: TaskStatus }
  >({
    mutationFn: async ({ id, status }) => {
      try {
        return await apiFetch<{ task: TaskDetail }>(`/tasks/${id}/status`, {
          method: "PUT",
          body: { status },
          token: accessToken,
        });
      } catch (err) {
        if (
          err instanceof ApiError &&
          err.status === 422 &&
          err.details &&
          typeof err.details === "object"
        ) {
          const d = err.details as {
            error?: string;
            message?: string;
            incompleteCount?: number;
          };
          if (d.error === "INCOMPLETE_ACTIONS") {
            return {
              incompleteActions: {
                code: "INCOMPLETE_ACTIONS",
                incompleteCount: d.incompleteCount ?? 0,
                message: d.message ?? "Actions still open",
              },
            };
          }
        }
        throw err;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY] });
      qc.invalidateQueries({ queryKey: [...TASKS_KEY, "detail", vars.id] });
    },
  });
}

export function useDeleteTask() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/tasks/${id}`, { method: "DELETE", token: accessToken }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...TASKS_KEY] });
    },
  });
}

export function useTaskActions(taskId: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...TASKS_KEY, taskId, "actions", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!taskId,
    queryFn: () =>
      apiFetch<PaginatedResponse<any>>(`/tasks/${taskId}/actions`, {
        token: accessToken,
        query: { page: 1, limit: 100 },
      }),
  });
}

export function useTaskComments(taskId: string) {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...TASKS_KEY, taskId, "comments", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!taskId,
    queryFn: async () => {
      const res = await apiFetch<{ data: TaskComment[] }>(
        `/tasks/${taskId}/comments`,
        { token: accessToken },
      );
      return res.data;
    },
  });
}

export function useAddComment(taskId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      apiFetch<{ comment: TaskComment }>(`/tasks/${taskId}/comments`, {
        method: "POST",
        body: { content },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...TASKS_KEY, taskId, "comments"],
      });
    },
  });
}

export function useDeleteComment(taskId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/tasks/${taskId}/comments/${commentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...TASKS_KEY, taskId, "comments"],
      });
    },
  });
}
